import React, { useState, useEffect } from 'react';
import { Slider, Switch, Typography, Spin } from 'antd';
import { getApiUrl } from '../utils/apiUrl';

const { Text } = Typography;

// 分辨率像素尺寸（标准值，不随模型变化）
const RATIOS = {
  '16:9': { '480p': [854,480], '720p': [1280,720], '1080p': [1920,1080] },
  '9:16': { '480p': [480,854], '720p': [720,1280], '1080p': [1080,1920] },
  '1:1':  { '480p': [480,480], '720p': [720,720], '1080p': [1080,1080] },
};
const RL = {
  '16:9': { n:'16:9 横屏', d:{ '480p':'854×480','720p':'1280×720','1080p':'1920×1080' } },
  '9:16': { n:'9:16 竖屏', d:{ '480p':'480×854','720p':'720×1280','1080p':'1080×1920' } },
  '1:1':  { n:'1:1 方屏', d:{ '480p':'480×480','720p':'720×720','1080p':'1080×1080' } },
};
const FPS = 24;

const btn = (a) => ({
  border: `1px solid ${a ? '#1677ff' : '#555'}`, borderRadius: 6, padding: '4px 14px',
  fontSize: 12, cursor: 'pointer', fontFamily: 'monospace', transition: 'all .15s',
  background: a ? '#1677ff' : '#3a3a3a', color: a ? '#fff' : '#aaa',
});

export default function VideoCostCalculator() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modelKey, setModelKey] = useState('');
  const [res, setRes] = useState('720p');
  const [ratio, setRatio] = useState('16:9');
  const [dur, setDur] = useState(5);
  const [hasRef, setHasRef] = useState(false);
  const [inDur, setInDur] = useState(5);

  // 从 API 获取 seedance 系列视频模型
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await fetch(getApiUrl('/api/models'));
        const data = await resp.json();
        if (data.code === 0) {
          const videoModels = (data.data.list || [])
            .filter(m => m.type === 'video')
            .map(m => {
              let meta = {};
              try { meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata) : (m.metadata || {}); } catch(e) {}
              // 获取该模型支持的分辨率列表
              const resolutions = (meta.resolutions || []).map(r => typeof r === 'string' ? r : r.value);
              // 无参考视频价格 / 有参考视频折扣价
              const rp = meta.resolution_pricing || {};
              const rpWithVideo = meta.resolution_pricing_with_video || {};
              return {
                name: m.model_name,
                label: m.display_name || meta.version || m.model_name,
                full: m.display_name || m.model_name,
                price: parseFloat(m.input_price) || 0,
                resolutions: resolutions.length > 0 ? resolutions : ['720p'],
                resolutionPrices: rp,
                resolutionPricesWithVideo: rpWithVideo,
              };
            })
            .sort((a, b) => b.price - a.price); // 价格从高到低排列
          if (videoModels.length > 0) {
            setModels(videoModels);
            setModelKey(videoModels[0].name);
            // 设第一个模型默认分辨率
            const firstRes = videoModels[0].resolutions[0] || '720p';
            setRes(firstRes);
          }
        }
      } catch(e) {
        console.log('获取视频模型失败:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchModels();
  }, []);

  const sel = models.find(m => m.name === modelKey) || models[0] || { name: '', label: '', price: 0, resolutions: ['720p'], resolutionPrices: {}, resolutionPricesWithVideo: {} };
  // 有参考视频时用 with_video 折扣价，否则用标准价（与网关 billing 逻辑一致）
  const resPrice = hasRef
    ? (sel.resolutionPricesWithVideo[res] || sel.resolutionPrices[res] || sel.price)
    : (sel.resolutionPrices[res] || sel.price);
  const [vw, vh] = (RATIOS[ratio] || RATIOS['16:9'])[res] || [1280,720];
  const outF = dur * FPS + 1;
  // BytePlus 官方公式: input duration 独立于 output，范围 2-15s，最低 4s
  const refDur = hasRef ? Math.max(inDur, 4) : 0;
  const inF = hasRef ? (refDur * FPS + 1) : 0;
  const tokens = Math.floor((inF + outF) * vw * vh / 1024);
  const cost = tokens * resPrice / 1000000;
  const keys = Object.keys(RATIOS);

  if (loading) return <div style={{ flex:1, display:'flex', justifyContent:'center', alignItems:'center', minHeight:400, background:'#fff' }}><Spin size="large" /></div>;

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px 24px 84px', background: '#fff' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 400px', gap: 56, alignItems: 'start', maxWidth: 1040, margin: '0 0 0 0' }}>
        {/* 左侧 */}
        <div style={{ paddingTop: 20 }}>
          <div style={{ color: '#52c41a', fontFamily: 'monospace', fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16 }}>
            {sel.label}
          </div>
          <h2 style={{ fontSize: 36, fontWeight: 800, margin: '0 0 24px', color: '#0a0a0a', lineHeight: 1.2, letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}>
            {sel.label}
          </h2>
          <h3 style={{ fontSize: 28, fontWeight: 700, margin: '-16px 0 24px', color: '#333', lineHeight: 1.2 }}>
            视频 Token 估算
          </h3>
          <p style={{ fontSize: 17, lineHeight: 1.75, color: '#555', margin: '0 0 28px', maxWidth: 540 }}>
            调整模型、分辨率与时长，估算单次调用消耗的 token。实际计费以任务完成时返回的 <Text code style={{ fontSize: 15 }}>usage</Text> 为准。
          </p>
          <div style={{
            background: '#e6f4ff', borderRadius: 10, padding: '10px 22px',
            fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 16, color: '#1d4ed8',
            lineHeight: 1.6, border: '1px solid #bae0ff', overflowX: 'auto', whiteSpace: 'nowrap',
          }}>
            tokens = (input + output) × W × H × fps / 1024
          </div>
          <p style={{ fontSize: 13, color: '#888', margin: '16px 0 0', lineHeight: 1.7, background: '#f5f5f5', borderRadius: 6, padding: '10px 14px' }}>
            实际用量以 <Text code style={{ fontSize: 12 }}>usage.completion_tokens</Text> 为准。价格从数据库模型元数据读取。
          </p>
        </div>

        {/* 右侧计算器 */}
        <div>
          <div style={{
            background: '#2c2c2c', borderRadius: 14, padding: 24,
            boxShadow: '0 8px 32px rgba(0,0,0,.28)',
          }}>
            {/* Display */}
            <div style={{
              background: '#1a1a2e', borderRadius: 10, padding: '16px 20px', marginBottom: 20,
              border: '1px solid #333',
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#999', marginBottom: 8, background: '#0d0d1a', borderRadius: 6, padding: '8px 12px' }}>
                ({hasRef ? refDur : '0'} + {dur}) × {vw} × {vh} × {FPS} / 1024 = <span style={{ color: '#4fc3f7', fontWeight: 700 }}>{tokens.toLocaleString()}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ color: '#888', fontSize: 10, marginBottom: 2 }}>预估 Token</div>
                  <div style={{ color: '#69f0ae', fontSize: 26, fontWeight: 700, fontFamily: 'monospace' }}>{tokens.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: 10, marginBottom: 2 }}>预估费用</div>
                  <div style={{ color: '#ffb74d', fontSize: 26, fontWeight: 700, fontFamily: 'monospace' }}>${cost.toFixed(4)}</div>
                </div>
              </div>
              <div style={{ color: '#555', fontSize: 10, marginTop: 8 }}>{vw}×{vh} · {res} · {dur}s · {sel.full} · ${resPrice}/1M{hasRef ? ' · 含参考视频(折扣价)' : ''}</div>
            </div>

            {/* Model */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#999', fontSize: 10, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>模型</div>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, whiteSpace: 'nowrap' }}>
                {models.map(m => <button key={m.name} style={{...btn(modelKey === m.name), flexShrink: 0}} onClick={() => { setModelKey(m.name); setRes(m.resolutions[0] || '720p'); }}>{m.label}</button>)}
              </div>
            </div>

            {/* Resolution */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#999', fontSize: 10, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>分辨率</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {sel.resolutions.map(r => <button key={r} style={btn(res === r)} onClick={() => setRes(r)}>{r}</button>)}
              </div>
            </div>

            {/* Ratio */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#999', fontSize: 10, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>画面比例</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {keys.map(k => (
                  <button key={k} style={{
                    border: `1px solid ${ratio === k ? '#1677ff' : '#555'}`, borderRadius: 6, padding: '6px 10px',
                    cursor: 'pointer', fontFamily: 'monospace', transition: 'all .15s',
                    background: ratio === k ? '#1677ff' : '#3a3a3a', color: ratio === k ? '#fff' : '#aaa',
                    textAlign: 'left', flex: 1,
                  }} onClick={() => setRatio(k)}>
                    <div style={{ fontSize: 12 }}>{RL[k].n}</div>
                    <div style={{ fontSize: 10, color: ratio === k ? 'rgba(255,255,255,.7)' : '#777', marginTop: 3 }}>{RL[k].d[res]}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Ref video */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#aaa', fontSize: 12 }}>
                <Switch checked={hasRef} onChange={setHasRef} size="small" />输入含参考视频 (最低 4s)
              </label>
              {hasRef && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
                    <span>输入时长 (最低 4s)</span><span style={{ color: '#fa8c16', fontSize: 13, fontWeight: 600 }}>{refDur}s</span>
                  </div>
                  <Slider min={1} max={15} value={inDur} onChange={setInDur}
                    styles={{ track: { background: '#fa8c16' }, rail: { background: '#444' }, handle: { borderColor: '#fa8c16' } }} />
                </div>
              )}
            </div>

            {/* Duration */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
                <span>输出时长</span><span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{dur}s</span>
              </div>
              <Slider min={3} max={15} value={dur} onChange={setDur}
                styles={{ track: { background: '#1677ff' }, rail: { background: '#444' }, handle: { borderColor: '#1677ff' } }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', fontSize: 10 }}><span>3s</span><span>15s</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
